import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { codeApi } from '@/domains/system-management/apis/code-management';
import type { Code, Params, UpdateCodeResponse } from '@/domains/system-management/types/code';
import NoticeMessage from '@/plugin/noticemessage/noticemessage';

export const useCodeManagement = (params: Params) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  // Get code list
  const useCodes = () => {
    return useQuery({
      queryKey: ['codes', params],
      queryFn: () => codeApi.getCodes(params),
    });
  };

  // Get code list for select
  const useActiveCodes = () => {
    const staticParams = { deletion: '001002' };
    return useQuery({
      queryKey: ['activeCodes', staticParams],
      queryFn: () => codeApi.getCodes(staticParams),
    });
  };

  // Get code details
  const useCode = (id: string) => {
    return useQuery({
      queryKey: ['code', id],
      queryFn: () => codeApi.getCode(id),
      enabled: !!id,
    });
  };

  // Create code
  const useCreateCode = (onCreateSuccess?: (response: { id: string }) => void) => {
    return useMutation({
      mutationFn: (data: Partial<Code>) => codeApi.createCode(data),
      onSuccess: (responseData) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > registration success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['codes', params] });
            if (onCreateSuccess) onCreateSuccess(responseData);
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  // Create lower code
  const useCreateLowerCode = (onCreateSuccess?: (response: { id: string }) => void) => {
    return useMutation({
      mutationFn: (data: Partial<Code>) => codeApi.createLowerCode(data),
      onSuccess: (responseData) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > registration success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['codes', params] });
            if (onCreateSuccess) onCreateSuccess(responseData);
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  // Update code
  const useUpdateCode = (onUpdateSuccess?: (response: UpdateCodeResponse) => void) => {
    return useMutation({
      mutationFn: ({ id, data }: { id: string; data: Partial<Code> }) =>
        codeApi.updateCode(id, data),
      onSuccess: (responseData) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > update success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['codes', params] });
            queryClient.invalidateQueries({
              queryKey: ['code', responseData.id],
            });
            if (onUpdateSuccess) onUpdateSuccess(responseData);
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  // Update code
  const useUpdateLowerCode = (onUpdateSuccess?: (response: UpdateCodeResponse) => void) => {
    return useMutation({
      mutationFn: ({ id, data }: { id: string; data: Partial<Code> }) =>
        codeApi.updateLowerCode(id, data),
      onSuccess: (responseData) => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > update success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['codes', params] });
            queryClient.invalidateQueries({
              queryKey: ['code', responseData.id],
            });
            if (onUpdateSuccess) onUpdateSuccess(responseData);
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  // Delete code
  const useDeleteCode = (onDeleteSuccess?: () => void) => {
    return useMutation({
      mutationFn: (id: string) => codeApi.deleteCode(id),
      onSuccess: () => {
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t('msg > delete success'), {
          callback() {
            queryClient.invalidateQueries({ queryKey: ['codes', params] });
            if (onDeleteSuccess) onDeleteSuccess();
          },
        });
      },
      onError: (error: unknown) => {
        const axiosError = error as AxiosError<{ message: string }>;
        const message = axiosError.response?.data?.message || axiosError.message;
        // TODO: Replace NoticeMessage with React-based toast/snackbar pattern
        new NoticeMessage(t(message));
      },
    });
  };

  return {
    useActiveCodes,
    useCodes,
    useCode,
    useCreateCode,
    useCreateLowerCode,
    useUpdateCode,
    useUpdateLowerCode,
    useDeleteCode,
  };
};
